/**
 * src/shell/landing/PortalLandingView.tsx
 *
 * Encapsulated public portal view for herx.org, sharing the exact same
 * shell design system, header, theme tokens, and modal triggers as mesh.herx.org.
 * Now seamlessly integrates the interactive SocialPortalView (Channels, 100% Organic
 * Chronological Feed, Sovereign Score Card, and Peer Vouching).
 */

import React, { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation.js';
import {
  ShieldGovernanceIcon,
  SparklesIcon,
  GlobeMeridiansIcon,
  ActivityIcon,
  PackageBoxIcon,
  UsersIcon,
} from '../icons.js';
import { SocialPortalView } from '../portal/SocialPortalView.js';
import { Radio, Info } from 'lucide-react';

interface PortalLandingViewProps {
  onOpenDePINMarket?: () => void;
  onOpenAccountSwitcher?: () => void;
  onEnterWorkspace?: () => void;
  currentUserDisplayName?: string;
  currentUserHandle?: string;
}

export const PortalLandingView: React.FC<PortalLandingViewProps> = ({
  onOpenDePINMarket,
  onOpenAccountSwitcher,
  onEnterWorkspace,
  currentUserDisplayName,
  currentUserHandle,
}) => {
  const { t } = useTranslation();
  const [subView, setSubView] = useState<'interactive-feed' | 'overview'>('interactive-feed');

  if (subView === 'interactive-feed') {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <SocialPortalView
          currentUserDisplayName={currentUserDisplayName}
          currentUserHandle={currentUserHandle}
          onSwitchToWorkspace={onEnterWorkspace}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        backgroundColor: 'var(--theme-bg-surface, #0b1120)',
        color: 'var(--theme-text-primary, #f8fafc)',
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div style={{ maxWidth: '1080px', width: '100%' }}>
        {/* Top return pill */}
        <div style={{ marginBottom: 20 }}>
          <button
            type="button"
            onClick={() => setSubView('interactive-feed')}
            style={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: 8,
              color: '#38bdf8',
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            ← Return to Live Social Feed & Channels
          </button>
        </div>

        {/* ── Hero Banner ──────────────────────────────────────────────────────── */}
        <section
          style={{
            textAlign: 'center',
            padding: '48px 16px',
            borderRadius: '24px',
            background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.6) 100%)',
            border: '1px solid var(--theme-border, #1e293b)',
            boxShadow: '0 20px 40px -15px rgba(0,0,0,0.5)',
            marginBottom: '32px',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 14px',
              borderRadius: '9999px',
              backgroundColor: 'rgba(56, 189, 248, 0.1)',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              color: '#38bdf8',
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '20px',
            }}
          >
            <SparklesIcon size={14} />
            <span>Public Freemium Portal • Decentralized Coordination Mesh</span>
          </div>

          <h1
            style={{
              fontSize: 'clamp(28px, 4vw, 44px)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              margin: '0 0 16px 0',
              background: 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Sovereign Social Cloud &amp; Zero-Trust Coordination
          </h1>

          <p
            style={{
              fontSize: '16px',
              color: 'var(--theme-text-secondary, #94a3b8)',
              maxWidth: '680px',
              margin: '0 auto 32px auto',
              lineHeight: 1.6,
            }}
          >
            Experience authentic human connection with 100% guaranteed organic reach. Zero surveillance ads,
            decentralized peer vouches, and micro-task economic coordination.
          </p>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setSubView('interactive-feed')}
              style={{
                padding: '12px 28px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#ffffff',
                border: 'none',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Radio size={16} />
              <span>Enter Live Social Feed</span>
            </button>

            {onEnterWorkspace && (
              <button
                id="portal-btn-enter-workspace"
                onClick={onEnterWorkspace}
                style={{
                  padding: '12px 28px',
                  borderRadius: '10px',
                  backgroundColor: 'var(--theme-bg-card, #151d2a)',
                  color: 'var(--theme-text-primary, #f8fafc)',
                  border: '1px solid var(--theme-border, #1e293b)',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <PackageBoxIcon size={16} />
                <span>Launch Enterprise Workspace</span>
              </button>
            )}

            {onOpenDePINMarket && (
              <button
                id="portal-btn-depin-market"
                onClick={onOpenDePINMarket}
                style={{
                  padding: '12px 28px',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(56, 189, 248, 0.1)',
                  color: '#38bdf8',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span>⚡</span>
                <span>Explore DePIN Marketplace</span>
              </button>
            )}
          </div>
        </section>

        {/* ── Feature Pillars Grid ───────────────────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '40px',
          }}
        >
          {/* Pillar 1 */}
          <div
            style={{
              padding: '24px',
              borderRadius: '16px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
            }}
          >
            <div style={{ color: '#38bdf8', marginBottom: '12px' }}>
              <ShieldGovernanceIcon size={24} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px 0' }}>100% Organic Reach</h3>
            <p style={{ fontSize: '13px', color: 'var(--theme-text-secondary, #94a3b8)', lineHeight: 1.5, margin: 0 }}>
              Chronological feeds guarantee your posts reach all subscribers without algorithm throttling or pay-to-play boosts.
            </p>
          </div>

          {/* Pillar 2 */}
          <div
            style={{
              padding: '24px',
              borderRadius: '16px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
            }}
          >
            <div style={{ color: '#10b981', marginBottom: '12px' }}>
              <ActivityIcon size={24} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px 0' }}>Peer Vouching & Slashing</h3>
            <p style={{ fontSize: '13px', color: 'var(--theme-text-secondary, #94a3b8)', lineHeight: 1.5, margin: 0 }}>
              Unfakeable human social graph where vouchers stake tokens. Malicious actors lose 50% of their voucher stake.
            </p>
          </div>

          {/* Pillar 3 */}
          <div
            style={{
              padding: '24px',
              borderRadius: '16px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
            }}
          >
            <div style={{ color: '#a855f7', marginBottom: '12px' }}>
              <GlobeMeridiansIcon size={24} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px 0' }}>Sovereign Reputation</h3>
            <p style={{ fontSize: '13px', color: 'var(--theme-text-secondary, #94a3b8)', lineHeight: 1.5, margin: 0 }}>
              Transparent 0–300 Sovereign Score rewarding hardware passkeys, high-signal content, and DePIN bandwidth relaying.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
